import { useEffect, useRef, useState } from 'react';
import {
  COOKS_MAP_KEY,
  cookedAgoLabel,
  scaleIngredientForDisplay,
  type CookLogEntry,
  type Recipe,
} from '@awwyeah/shared';
import { ensureStructured } from '../utils/ensureStructured';
import { ArrowLeft, Camera, CookingPot, ListPlus, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../../auth';
import { logCook, unlogCook, useCooksDoc, useLastCook } from '../cooks';
import { useHouseholdRoster } from '../useHouseholdRoster';
import { IngredientPickerSheet } from '../components/IngredientPickerSheet';
import { RecipePhoto } from '../components/RecipePhoto';
import { ScaleControls } from '../components/ScaleControls';
import { Toast, type ToastAction } from '../components/Toast';
import {
  RECIPES_MAP_KEY,
  deleteRecipe,
  restoreRecipe,
  setRecipePhotoRef,
  useRecipe,
  useRecipesDoc,
} from '../hooks';
import { localPhotoRef, preparePhoto } from '../outbox/image';
import { readStoredScale, writeStoredScale } from '../utils/scalePreference';
import { getRecipePhotoOutbox, onRecipePhotoUploaded, uploadedRecipePhotoRef } from '../outbox';
import {
  readStoredUnitsPreference,
  unitsPreferenceToSystem,
  writeStoredUnitsPreference,
  type UnitsPreference,
} from '../utils/unitsPreference';

/**
 * Handed to this page by `CookModePage` when the cook was logged from the end of the steps
 * (`navigate(..., { state })`). Cook mode is a full-screen layer that unmounts on the way
 * out, so it has nowhere to show its own undo toast — it logs the cook, leaves, and lets
 * the page you land on offer the undo.
 */
interface CookHandoffState {
  cookedEntryId?: string;
  cookedTitle?: string;
}

export function RecipeDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const doc = useRecipesDoc();
  const recipe = useRecipe(doc, id);
  const cooksDoc = useCooksDoc();
  const lastCook = useLastCook(cooksDoc, id);
  const { getMember } = useHouseholdRoster();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [toast, setToast] = useState<{
    title: string;
    subtitle?: string;
    action?: ToastAction;
  } | null>(null);
  // Scale factor is remembered per recipe, per device (`utils/scalePreference.ts`) and shared
  // with cook mode, so "Makes 8" survives a trip into cook mode and back. The units
  // preference is per device across all recipes.
  const [factor, setFactorState] = useState(() =>
    id ? (readStoredScale(window.localStorage, id) ?? 1) : 1,
  );
  function setFactor(next: number): void {
    setFactorState(next);
    if (id) writeStoredScale(window.localStorage, id, next);
  }
  // The route reuses this component across recipes — pick up the new recipe's own amount.
  useEffect(() => {
    if (id) setFactorState(readStoredScale(window.localStorage, id) ?? 1);
  }, [id]);
  const [unitsPreference, setUnitsPreference] = useState<UnitsPreference>(() =>
    readStoredUnitsPreference(window.localStorage),
  );
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [regrabBusy, setRegrabBusy] = useState(false);

  /** Owner ask 2026-08-04: refetch the source page's image for URL-having recipes. The
   * server fetches/stores (SSRF-guarded) and this client writes the ref (§1.3). */
  const regrabPhoto = async (): Promise<void> => {
    if (!recipe?.sourceUrl || !user) return;
    setRegrabBusy(true);
    try {
      const res = await fetch('/api/recipes/refetch-photo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: recipe.sourceUrl }),
      });
      if (res.ok) {
        const data = (await res.json()) as { photoRef: Recipe['photoRef'] };
        if (data.photoRef) {
          const map = doc.getMap<Recipe>(RECIPES_MAP_KEY);
          setRecipePhotoRef(map, recipe.id, data.photoRef, user.id);
        }
      }
    } finally {
      setRegrabBusy(false);
    }
  };

  /**
   * Upload/change photo (WS-20): downscale + EXIF-strip on-device (same pipeline as
   * check-in's outbox), write `photoRef.state: 'local'` immediately so the new photo
   * renders from the local blob right away, queue the bytes for upload, and flip to
   * `'uploaded'` when the outbox reports success. A retry after a network hiccup is
   * automatic — the outbox owns that, same as check-in's.
   */
  async function handlePhotoChosen(file: File | undefined): Promise<void> {
    if (!file || !recipe || !user) return;
    setPhotoBusy(true);
    try {
      const map = doc.getMap<Recipe>(RECIPES_MAP_KEY);
      const prepared = await preparePhoto(file);
      setRecipePhotoRef(map, recipe.id, localPhotoRef(prepared), user.id);
      await getRecipePhotoOutbox().queuePhoto({
        id: prepared.id,
        recipeId: recipe.id,
        blob: prepared.blob,
        contentType: prepared.contentType,
        width: prepared.width,
        height: prepared.height,
      });
    } finally {
      setPhotoBusy(false);
    }
  }

  useEffect(() => {
    if (!recipe) return;
    const recipeId = recipe.id;
    return onRecipePhotoUploaded((record) => {
      if (record.recipeId !== recipeId) return;
      const map = doc.getMap<Recipe>(RECIPES_MAP_KEY);
      const current = map.get(recipeId);
      if (current?.photoRef?.id !== record.id) return; // superseded by a newer upload
      setRecipePhotoRef(map, recipeId, uploadedRecipePhotoRef(current.photoRef), current.updatedBy);
    });
  }, [doc, recipe?.id]);

  /** The undo toast for a cook, wherever the cook was logged from. Undo is a real delete
   * (`unlogCook`) — a mis-tapped "Cooked this" should leave no trace in the timeline. */
  function cookedToast(title: string, cookId: string): { title: string; action: ToastAction } {
    return {
      title: `Cooked "${title}"`,
      action: {
        label: 'Undo',
        onClick: () => unlogCook(cooksDoc.getMap<CookLogEntry>(COOKS_MAP_KEY), cookId),
      },
    };
  }

  /**
   * Picks up a cook logged from the end of cook mode and shows its undo toast here, then
   * replaces the history entry so the toast is offered exactly once — without the replace,
   * a back-navigation or a reload would re-surface an undo for a cook that is by then old
   * news (or already undone).
   */
  useEffect(() => {
    const handoff = (location.state as CookHandoffState | null) ?? null;
    if (!handoff?.cookedEntryId) return;
    setToast(cookedToast(handoff.cookedTitle ?? 'it', handoff.cookedEntryId));
    navigate(location.pathname, { replace: true });
  }, [location.key]);

  function handleUnitsPreferenceChange(next: UnitsPreference): void {
    setUnitsPreference(next);
    writeStoredUnitsPreference(window.localStorage, next);
  }

  const toSystem = unitsPreferenceToSystem(unitsPreference);

  if (!recipe) {
    return (
      <div
        className="flex flex-col items-center gap-3 px-4 py-16 text-center"
        data-testid="recipe-not-found"
      >
        <p className="text-[15px] text-fg-2">This recipe isn&apos;t here anymore.</p>
        <Link to="/recipes" className="text-[13px] font-semibold text-accent">
          Back to the box
        </Link>
      </div>
    );
  }

  function handleDelete(): void {
    if (!recipe) return;
    const map = doc.getMap<Recipe>(RECIPES_MAP_KEY);
    const removed = deleteRecipe(map, recipe.id);
    void navigate('/recipes');
    if (!removed) return;
    setToast({
      title: `Deleted "${removed.title}"`,
      action: { label: 'Undo', onClick: () => restoreRecipe(map, removed) },
    });
  }

  /** "Cooked this" (owner ask 2026-08-18). One tap, no dialog, no rating, no "how did it
   * go?" form — the whole affordance is the fact that you made the thing. */
  function handleCookedThis(): void {
    if (!recipe || !user) return;
    const entry = logCook(cooksDoc.getMap<CookLogEntry>(COOKS_MAP_KEY), recipe, user.id);
    setToast(cookedToast(recipe.title, entry.id));
  }

  return (
    <div className="flex min-h-full flex-col pb-24">
      <header className="flex items-center gap-2 px-2 py-3">
        <button
          type="button"
          onClick={() => void navigate('/recipes')}
          aria-label="Back to recipes"
          className="flex h-11 w-11 shrink-0 items-center justify-center text-fg-2"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <h1 className="min-w-0 flex-1 truncate font-display text-lg tracking-tight text-fg">
          {recipe.title}
        </h1>
        <Link
          to={`/recipes/${recipe.id}/edit`}
          aria-label="Edit recipe"
          className="flex h-11 w-11 shrink-0 items-center justify-center text-fg-2"
        >
          <Pencil className="h-5 w-5" aria-hidden="true" />
        </Link>
        <button
          type="button"
          data-testid="recipe-delete-button"
          onClick={handleDelete}
          aria-label="Delete recipe"
          className="flex h-11 w-11 shrink-0 items-center justify-center text-danger"
        >
          <Trash2 className="h-5 w-5" aria-hidden="true" />
        </button>
      </header>

      <div className="relative h-40">
        <RecipePhoto recipe={recipe} alt={recipe.title} variant="display" className="h-40" />
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          data-testid="recipe-photo-input"
          onChange={(e) => void handlePhotoChosen(e.target.files?.[0])}
        />
        <button
          type="button"
          data-testid="recipe-photo-button"
          disabled={photoBusy}
          onClick={() => photoInputRef.current?.click()}
          className="absolute bottom-2 right-2 flex h-9 items-center gap-1.5 rounded-full bg-inverse px-3 text-xs font-semibold text-inverse-fg shadow-sm disabled:opacity-60"
        >
          <Camera className="h-4 w-4" aria-hidden="true" />
          {photoBusy ? 'Uploading…' : recipe.photoRef ? 'Change photo' : 'Add photo'}
        </button>
        {recipe.sourceUrl && (
          <button
            type="button"
            data-testid="recipe-regrab-button"
            disabled={regrabBusy}
            onClick={() => void regrabPhoto()}
            title="Fetch the photo from the recipe's source page"
            className="absolute bottom-2 left-2 flex h-9 items-center gap-1.5 rounded-full border border-line bg-surface px-3 text-xs font-semibold text-fg-2 shadow-sm disabled:opacity-60"
          >
            <RefreshCw
              className={`h-4 w-4 ${regrabBusy ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            {regrabBusy ? 'Grabbing…' : 'Re-grab'}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2 border-b border-line px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          {recipe.tags.map((tag) => (
            <span
              key={tag}
              className="flex h-7 items-center rounded-full bg-raised px-2.5 text-xs font-semibold text-fg"
            >
              {tag}
            </span>
          ))}
          {recipe.servings && <span className="text-xs text-fg-3">serves {recipe.servings}</span>}
        </div>
        {recipe.notes && <p className="text-[13px] text-fg-3">{recipe.notes}</p>}
        {recipe.sourceUrl && (
          <a
            href={recipe.sourceUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="recipe-source-link"
            className="truncate text-[13px] font-semibold text-accent"
          >
            {recipe.sourceUrl}
          </a>
        )}
      </div>

      {recipe.ingredients.length > 0 && (
        <ScaleControls
          baseServings={recipe.servings}
          factor={factor}
          onFactorChange={setFactor}
          unitsPreference={unitsPreference}
          onUnitsPreferenceChange={handleUnitsPreferenceChange}
        />
      )}

      {(recipe.ingredients.length > 0 || recipe.steps.length > 0) && (
        <div className="px-4 pt-3">
          <button
            type="button"
            data-testid="recipe-cook-button"
            onClick={() =>
              void navigate(`/recipes/${recipe.id}/cook`, { state: { factor, unitsPreference } })
            }
            className="flex h-[52px] w-full items-center justify-center gap-2 rounded-md bg-inverse text-[16px] font-semibold text-inverse-fg"
          >
            <CookingPot className="h-5 w-5" aria-hidden="true" />
            Cook
          </button>
        </div>
      )}

      {/* "Cooked this" sits *beside* Cook rather than inside it: cook mode is for the next
          hour, this is for the thing you already did — including the recipes you know by
          heart and never open cook mode for at all. Always available, so a recipe with no
          steps (the smoked-salmon case that prompted this) can still be logged. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-4 pt-3">
        <button
          type="button"
          data-testid="recipe-cooked-this"
          onClick={handleCookedThis}
          className="flex h-11 items-center gap-2 rounded-md border-2 border-line-strong px-4 text-[15px] font-semibold text-fg active:bg-raised"
        >
          <CookingPot className="h-[18px] w-[18px]" aria-hidden="true" />
          Cooked this
        </button>
        {/* A count of cooks is deliberately absent (owner call 2026-08-18) — this line names
            the last night somebody made it and stops there. Never cooked renders nothing at
            all rather than an empty state, which would only ever read as a nag. */}
        {lastCook && (
          <span data-testid="recipe-last-cooked" className="text-[13px] text-fg-3">
            Last cooked {cookedAgoLabel(lastCook.cookedAt)} by{' '}
            <span style={{ color: getMember(lastCook.cookedBy).color }} className="font-semibold">
              {getMember(lastCook.cookedBy).name}
            </span>
          </span>
        )}
      </div>

      <div className="flex items-baseline justify-between px-4 pb-1 pt-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-3">
          Ingredients · {recipe.ingredients.length}
        </span>
        {recipe.ingredients.length > 0 && (
          <button
            type="button"
            data-testid="recipe-pick-ingredients"
            onClick={() => setPickerOpen(true)}
            className="text-[13px] font-semibold text-accent"
          >
            Pick some
          </button>
        )}
      </div>
      <div className="flex flex-col px-4">
        {recipe.ingredients.map((ing) => {
          const display = scaleIngredientForDisplay(ensureStructured(ing), factor, toSystem);
          return (
            <div key={ing.id} className="flex min-h-10 items-center gap-2.5 border-b border-line">
              <span className="h-1 w-1 shrink-0 rounded-full bg-fg-3" />
              <span className="flex-1 text-[15px] text-fg" data-testid="ingredient-line">
                {display.approx && (
                  <span
                    className="mr-0.5 text-fg-3"
                    title="Approximate — converted from volume via typical density"
                    aria-hidden="true"
                  >
                    ≈
                  </span>
                )}
                {display.approx ? display.text.replace(/^≈/, '') : display.text}
              </span>
            </div>
          );
        })}
        {recipe.ingredients.length === 0 && (
          <p className="py-3 text-[13px] text-fg-3">No ingredients listed yet.</p>
        )}
      </div>

      {recipe.steps.length > 0 && (
        <>
          <div className="px-4 pb-1 pt-4">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-3">
              Steps
            </span>
          </div>
          <div className="flex flex-col gap-2.5 px-4">
            {recipe.steps.map((step, i) => (
              <div key={i} className="flex gap-2.5">
                <span className="shrink-0 font-mono text-[13px] font-semibold text-accent">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-[15px] leading-relaxed text-fg-2">{step}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {recipe.ingredients.length > 0 && (
        <div className="fixed inset-x-0 bottom-14 z-10 flex gap-2 border-t border-line bg-surface px-4 py-2.5 lg:bottom-0">
          <button
            type="button"
            data-testid="recipe-add-all-to-list"
            onClick={() => setPickerOpen(true)}
            className="h-[52px] flex-1 rounded-md bg-inverse text-[16px] font-semibold text-inverse-fg"
          >
            Add ingredients to a list
          </button>
          <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-md border-2 border-line-strong">
            <ListPlus className="h-5 w-5 text-fg" aria-hidden="true" />
          </span>
        </div>
      )}

      {pickerOpen && user && (
        <IngredientPickerSheet
          recipe={recipe}
          userId={user.id}
          scaleFactor={factor}
          unitSystem={toSystem}
          onClose={() => setPickerOpen(false)}
          onAdded={(summary) => setToast(summary)}
        />
      )}

      {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
